/**
 * User-scoped in-memory cache for the /profile/analytics response.
 *
 * The analytics endpoint is slow (3-5s). This cache makes
 * navigate-away-and-back feel instant while avoiding a redundant API call.
 *
 * SECURITY: Scoped by user_id. The cache is cleared on logout so user A's
 * PII-laden analytics (question_text + student_response evidence, weakness
 * commentary) cannot be shown to user B on a shared device.
 *
 * Extracted into a dedicated module so App.js can import
 * resetAnalyticsCache() without breaking Progress.js's code-split boundary.
 */

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let cache = {
  userId: null,
  data: null,
  timestamp: 0,
};

/** Get cached analytics data if it matches the given user and is fresh. */
export function getCachedAnalytics(userId) {
  if (!userId || cache.userId !== String(userId)) return null;
  if (!cache.data) return null;
  if (Date.now() - cache.timestamp >= CACHE_TTL_MS) return null;
  return cache.data;
}

/** Store analytics data in the cache scoped to a specific user. */
export function setCachedAnalytics(userId, data) {
  cache = {
    userId: userId ? String(userId) : null,
    data,
    timestamp: Date.now(),
  };
}

/** Reset the analytics cache. Call on logout to prevent PII leak. */
// Owner debugging doc 2026-09-01: the Progress "Test prep" card's payload
// rides the same 5-minute per-user cache so a Progress remount does not
// re-fetch it (review #562).
let prepCache = { userId: null, data: null, timestamp: 0 };

export function getCachedPrepActivity(userId) {
  if (!userId || prepCache.userId !== String(userId)) return null;
  if (!prepCache.data) return null;
  if (Date.now() - prepCache.timestamp >= CACHE_TTL_MS) return null;
  return prepCache.data;
}

export function setCachedPrepActivity(userId, data) {
  prepCache = { userId: userId ? String(userId) : null, data, timestamp: Date.now() };
}

export function resetAnalyticsCache() {
  cache = { userId: null, data: null, timestamp: 0 };
  prepCache = { userId: null, data: null, timestamp: 0 };
}

/** Test-only: inspect the internal cache state. */
export function _getCacheState() {
  return { ...cache };
}
